import { z } from 'zod';
/**
 * The shared vocabulary of the platform. Everything here is a zod schema first and a
 * TypeScript type second, so the same definition validates at the API boundary and
 * types the web app and the driver PWA.
 */
// ── Roles ───────────────────────────────────────────────────────────────────────
/**
 * `platform_admin` is Milepost staff. It does NOT grant ambient access to tenant
 * data — reaching a carrier's records requires an explicit, audited impersonation
 * with a stated reason.
 */
export const UserRole = z.enum([
    'platform_admin',
    'carrier_admin',
    'safety_manager',
    'read_only',
    'driver',
]);
/** Roles that belong to a carrier workspace and can see the carrier UI. */
export const CARRIER_STAFF_ROLES = [
    'carrier_admin',
    'safety_manager',
    'read_only',
];
// ── Drivers ─────────────────────────────────────────────────────────────────────
/**
 * Drives the medical-certification branch, which changed on 2025-06-23 with the
 * Medical Examiner's Certification Integration rule:
 *
 *  - `cdl` / `clp`: medical examiners transmit results electronically to the FMCSA
 *    National Registry, which flows to the State Driver Licensing Agency and onto the
 *    MVR. A copy of the paper Medical Examiner's Certificate is NO LONGER valid proof
 *    in the DQ file, and NRCME verification is no longer required. The required
 *    artifact is an MVR showing current medical certification status.
 *  - `non_cdl`: unchanged. Paper MEC copy plus an NRCME verification note.
 */
export const DriverClass = z.enum(['cdl', 'clp', 'non_cdl']);
export const DriverStatus = z.enum([
    'applicant',
    'active',
    'inactive',
    'terminated',
]);
// ── Requirements ────────────────────────────────────────────────────────────────
/** How often a requirement must be re-satisfied. */
export const RecurrenceKind = z.enum([
    /** Must exist before the driver operates a CMV. */
    'pre_employment',
    /** Collected once at hire and never repeated (e.g. employment application). */
    'once_at_hire',
    /** Every 12 months (e.g. annual MVR, annual review of driving record). */
    'annual',
    /** Re-satisfied after each medical examination, whenever that happens. */
    'per_medical_exam',
    /** Hard deadline measured in days from the hire date (e.g. §391.23 at 30 days). */
    'within_days_of_hire',
    /** Every 365 days, counted from the previous occurrence (Clearinghouse queries). */
    'rolling_365',
    /**
     * No fixed interval — validity comes entirely from the artifact's own expiry date
     * (a licence, an SPE certificate, a federal exemption letter). There is nothing to
     * compute: if the document has no expiry on it, the requirement cannot be evaluated
     * and the carrier is told to supply one.
     */
    'artifact_expiry',
]);
/**
 * Retention clocks. Modeled as data because they start from different events and
 * getting them wrong in either direction is a finding: too short is destruction of a
 * required record, too long is holding PII with no lawful basis.
 */
export const RetentionRule = z.enum([
    /** Duration of employment plus 3 years — §391.51(c)/(d). */
    'employment_plus_3y',
    /** 3 years from the date the record was executed — §391.51(d)(1)-(6). */
    'execution_plus_3y',
    /** 3 years from the query date — §382.701. */
    'query_plus_3y',
    /**
     * 5 years from the TEST date, not from termination — §382.401(b)(1). Applies to
     * verified positives, refusals, and alcohol results of 0.02 or greater.
     */
    'test_date_plus_5y',
    /**
     * 1 year from the test date — §382.401(b)(1)(vi), for negative and cancelled
     * controlled-substances results. Deliberately distinct from the 5-year rule: applying
     * one clock to both would either destroy a record early or retain PII with no basis.
     */
    'test_date_plus_1y',
    /** 14 months from the inspection report date — §396.17 (v2). */
    'months_14',
    /** 1 year under carrier control plus 6 months after — §396.3(b) (v2). */
    'vehicle_control_plus_6mo',
    /** 3 months from the report date — §396.11 (v2). */
    'months_3',
    /** 6 months — §395.8(k) (v2). */
    'months_6',
    /** Kept for the life of the file with no independent purge date. */
    'indefinite',
]);
/** What actually satisfies a requirement. */
export const ArtifactKind = z.enum([
    /** An uploaded file (PDF or photo). */
    'document',
    /** A structured record entered in the app (e.g. a Clearinghouse query result). */
    'record',
    /** A signed attestation by carrier staff (e.g. the annual review note). */
    'attestation',
]);
/** Who is expected to produce the artifact — decides who gets nudged. */
export const ResponsibleParty = z.enum(['carrier', 'driver', 'either']);
export const RequirementStatus = z.enum([
    'satisfied',
    /** Satisfied today, but inside the alert window before it expires. */
    'expiring_soon',
    /** Was satisfied; the artifact's validity has lapsed. */
    'expired',
    /** Never satisfied. */
    'missing',
    /** A driver submitted something; carrier staff have not accepted it yet. */
    'pending_review',
    /** Does not apply to this driver (e.g. NRCME note for a CDL holder). */
    'not_applicable',
]);
/**
 * Statuses that mean the carrier is out of compliance right now.
 *
 * `pending_review` is included: a requirement only reaches that status when nothing
 * accepted stands behind it, so all the carrier holds is an unreviewed upload.
 */
export const BLOCKING_STATUSES = [
    'expired',
    'missing',
    'pending_review',
];
// ── Documents ───────────────────────────────────────────────────────────────────
/**
 * `restricted` documents are gated separately from the rest of the DQ file and
 * audited on every read. Drug and alcohol testing records and Clearinghouse results
 * must be kept confidential and separate under §40.331 and §382.401 — modeling this
 * from the first migration means the drug-and-alcohol module is not a retrofit.
 */
export const DocumentSensitivity = z.enum(['normal', 'restricted']);
export const DocumentReviewState = z.enum([
    /** Uploaded by a driver, awaiting carrier review. Never satisfies a requirement. */
    'pending_review',
    'accepted',
    'rejected',
    /** Superseded by a newer version of the same artifact. */
    'superseded',
]);
// ── Evaluation output ───────────────────────────────────────────────────────────
/**
 * One row of a driver's compliance picture. Carries the CFR citation so the UI never
 * asserts a rule without saying where it comes from — and so a carrier can check us.
 */
export const RequirementEvaluation = z.object({
    requirementCode: z.string(),
    title: z.string(),
    cfrCite: z.string(),
    status: RequirementStatus,
    artifactKind: ArtifactKind,
    responsibleParty: ResponsibleParty,
    sensitivity: DocumentSensitivity,
    /** When the satisfying artifact was executed/issued. */
    effectiveAt: z.string().datetime().nullable(),
    /** When it stops being valid. Null for non-expiring items. */
    expiresAt: z.string().datetime().nullable(),
    /** When the carrier must act. For `missing` items this may be in the past. */
    dueAt: z.string().datetime().nullable(),
    /** Negative once overdue. Null when there is nothing to count down to. */
    daysUntilDue: z.number().int().nullable(),
    /** The document or record that satisfied it, if any. */
    satisfiedBy: z.string().uuid().nullable(),
    /** Plain-language reason, shown in the UI and in alert emails. */
    explanation: z.string(),
});
export const DriverComplianceSummary = z.object({
    driverId: z.string().uuid(),
    /** True when nothing is expired or missing. */
    compliant: z.boolean(),
    counts: z.object({
        satisfied: z.number().int(),
        expiringSoon: z.number().int(),
        expired: z.number().int(),
        missing: z.number().int(),
        pendingReview: z.number().int(),
        notApplicable: z.number().int(),
    }),
    /** Soonest actionable due date across all requirements. */
    nextDueAt: z.string().datetime().nullable(),
    evaluatedAt: z.string().datetime(),
    catalogVersion: z.number().int(),
    requirements: z.array(RequirementEvaluation),
});
//# sourceMappingURL=domain.js.map